import { useEffect, useRef, useCallback } from 'react'
import { supabase } from './supabase'

const IDLE_MS        = 30 * 60 * 1000   // 30 min no activity → sign out
const AWAY_MS        = 15 * 60 * 1000   // 15 min tab hidden  → reload on return
const EVENTS         = ['mousemove','mousedown','keydown','touchstart','scroll','click']

export function useIdleTimeout(session) {
  const idleTimer  = useRef(null)
  const hiddenAt   = useRef(null)

  const resetIdle = useCallback(() => {
    clearTimeout(idleTimer.current)
    idleTimer.current = setTimeout(async () => {
      await supabase.auth.signOut()
      // brief pause so signOut completes, then hard-reload to login
      setTimeout(() => window.location.reload(), 300)
    }, IDLE_MS)
  }, [])

  useEffect(() => {
    // Only run when a session exists
    if (!session) return

    // Start the idle clock
    resetIdle()

    // Reset on any user activity
    EVENTS.forEach(e => window.addEventListener(e, resetIdle, { passive: true }))

    // Visibility change: track when tab was hidden; reload if away too long
    function handleVisibility() {
      if (document.hidden) {
        hiddenAt.current = Date.now()
      } else {
        const awayMs = hiddenAt.current ? Date.now() - hiddenAt.current : 0
        hiddenAt.current = null
        if (awayMs >= AWAY_MS) {
          // If session is still valid, just reload to get fresh data
          // The idle timer may have already signed them out — reload handles both
          window.location.reload()
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      clearTimeout(idleTimer.current)
      EVENTS.forEach(e => window.removeEventListener(e, resetIdle))
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [session, resetIdle])
}
